/**
 * Codice fiscale italiano: verifica e dati che contiene.
 *
 * Dal codice fiscale si ricavano con certezza (se il carattere di controllo
 * torna) data di nascita, sesso e codice catastale del comune o dello stato
 * estero di nascita. Il codice catastale lo traduce poi il backend nel comune
 * o nella nazione dell'anagrafica.
 */

const MESI = "ABCDEHLMPRST";
// Omocodia: in caso di codici uguali alcune cifre diventano lettere.
const OMOCODIA = "LMNPQRSTUV";
const POSIZIONI_NUMERICHE = [6, 7, 9, 10, 12, 13, 14];

// Valori dei caratteri in posizione dispari (la prima, la terza...): cifre e
// lettere nello stesso ordine, 0 e A valgono 1, 1 e B valgono 0, e cosi' via.
const VALORI_DISPARI = [1, 0, 5, 7, 9, 13, 15, 17, 19, 21, 2, 4, 18, 20, 11, 3, 6, 8, 12, 14, 16, 10, 22, 25, 24, 23];

function valoreDispari(c) {
  return c >= "0" && c <= "9" ? VALORI_DISPARI[c.charCodeAt(0) - 48] : VALORI_DISPARI[c.charCodeAt(0) - 65];
}

function valorePari(c) {
  return c >= "0" && c <= "9" ? c.charCodeAt(0) - 48 : c.charCodeAt(0) - 65;
}

/** Carattere di controllo dei primi 15 caratteri. */
export function carattereControllo(cf15) {
  let tot = 0;
  for (let i = 0; i < 15; i++) {
    tot += i % 2 === 0 ? valoreDispari(cf15[i]) : valorePari(cf15[i]);
  }
  return String.fromCharCode(65 + (tot % 26));
}

/** Toglie l'omocodia: le lettere al posto delle cifre tornano cifre. */
function senzaOmocodia(cf) {
  const c = cf.split("");
  for (const i of POSIZIONI_NUMERICHE) {
    const p = OMOCODIA.indexOf(c[i]);
    if (p >= 0) {
      c[i] = String(p);
    }
  }
  return c.join("");
}

const FORMA = /^[A-Z]{6}[0-9LMNPQRSTUV]{2}[ABCDEHLMPRST][0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{3}[A-Z]$/;

/**
 * Legge un codice fiscale.
 *
 * @param {string} testo il codice, anche con spazi o minuscole
 * @returns {object|null} null se non ha la forma di un codice fiscale
 */
export function leggiCodiceFiscale(testo) {
  const cf = String(testo || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (!FORMA.test(cf)) {
    return null;
  }
  const valido = carattereControllo(cf) === cf[15];
  const n = senzaOmocodia(cf);
  const aa = parseInt(n.substring(6, 8), 10);
  const mese = MESI.indexOf(n[8]) + 1;
  let giorno = parseInt(n.substring(9, 11), 10);
  const sesso = giorno > 40 ? "F" : "M";
  if (giorno > 40) {
    giorno -= 40;
  }
  if (giorno < 1 || giorno > 31) {
    return null;
  }
  const oggi = new Date().getFullYear() % 100;
  const anno = (aa > oggi ? 1900 : 2000) + aa;
  const pad = (x) => String(x).padStart(2, "0");
  return {
    codice: cf,
    valido,
    sesso,
    dataNascita: anno + "-" + pad(mese) + "-" + pad(giorno),
    catastale: n.substring(11, 15),
    estero: n[11] === "Z"
  };
}

/** Cerca un codice fiscale valido dentro un testo qualsiasi (codice a barre, OCR). */
export function trovaCodiceFiscale(testo) {
  const t = String(testo || "").toUpperCase();
  const trovati = t.match(/[A-Z]{6}[0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{3}[A-Z]/g) || [];
  for (const c of trovati) {
    const cf = leggiCodiceFiscale(c);
    if (cf && cf.valido) {
      return cf;
    }
  }
  return null;
}

/** Consonanti e vocali come le usa il codice fiscale per nome e cognome. */
function lettere(s) {
  const t = String(s || "").toUpperCase().replace(/[^A-Z]/g, "");
  return { cons: t.replace(/[AEIOU]/g, ""), voc: t.replace(/[^AEIOU]/g, "") };
}

/** Le tre lettere del cognome nel codice fiscale. */
export function codiceCognome(cognome) {
  const { cons, voc } = lettere(cognome);
  return (cons + voc + "XXX").substring(0, 3);
}

/** Le tre lettere del nome: con 4 o piu' consonanti si salta la seconda. */
export function codiceNome(nome) {
  const { cons, voc } = lettere(nome);
  if (cons.length >= 4) {
    return cons[0] + cons[2] + cons[3];
  }
  return (cons + voc + "XXX").substring(0, 3);
}

/** Il codice fiscale appartiene a questo cognome e nome? */
export function corrispondeA(cf, cognome, nome) {
  return cf.substring(0, 3) === codiceCognome(cognome) && cf.substring(3, 6) === codiceNome(nome);
}

export default { leggiCodiceFiscale, trovaCodiceFiscale, corrispondeA };
